import { useEffect, useState, Ref, useImperativeHandle, useMemo } from 'react';
import _ from 'lodash';
import {
    useWorkOrderPhotoTypesQuery,
    useWorkOrderVideoTypesQuery,
} from 'common/stores/redux/MetaStore/MetaStoreApi';
import {
    WorkOrderPhotoTypeAPI,
    WorkOrderVideoTypeAPI,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { Photo } from 'common/types/documentRequirements';
import TypeAheadSearchBar from 'common/components/form/TypeAheadSearchBar';

interface DocumentRequirementsPhotoSearchProps {
    ref?: Ref<{
        clearSelectedTypeList: () => void;
        removeSelectedTypeList: (id: string) => void;
    }>;
    mediaList: Photo[];
    disabled: boolean;
    onSearchChanged: (
        selectedTypeList:
            | (WorkOrderPhotoTypeAPI | WorkOrderVideoTypeAPI | undefined)[]
            | undefined
    ) => void;
}

const DocumentRequirementsPhotoSearch = ({
    mediaList,
    ref,
    disabled = false,
    onSearchChanged,
}: DocumentRequirementsPhotoSearchProps) => {
    const { data: workOrderPhotoTypes } = useWorkOrderPhotoTypesQuery();
    const { data: workOrderVideoTypes } = useWorkOrderVideoTypesQuery();
    let allMediaTypes: (WorkOrderPhotoTypeAPI | WorkOrderVideoTypeAPI | undefined)[] = [];
    if (workOrderPhotoTypes) {
        allMediaTypes = allMediaTypes.concat(
            workOrderPhotoTypes.map(v => ({ ...v, category: 'Photo' }))
        );
    }
    if (workOrderVideoTypes) {
        allMediaTypes = allMediaTypes.concat(
            workOrderVideoTypes.map(v => ({ ...v, category: 'Video' }))
        );
    }
    const [selectedTypeList, setSelectedTypeList] =
        useState<
            (WorkOrderPhotoTypeAPI | WorkOrderVideoTypeAPI | undefined)[]
        >();

    const clearSelectedTypeList = () => {
        setSelectedTypeList([]);
    };

    const removeSelectedTypeList = (id: string) => {
        const newList = _.reject(selectedTypeList, x => x?.id === id);
        setSelectedTypeList(newList);
    };

    useImperativeHandle(ref, () => ({
        clearSelectedTypeList,
        removeSelectedTypeList,
    }));

    const searchOptions = useMemo(() => {
        return _.chain(mediaList)
            .map(x => x.requirement.photoTypeId)
            .reject(x => _.some(selectedTypeList, y => y?.id === x))
            .uniq()
            .map(x => allMediaTypes?.find(y => y.id === x))
            .reject(x => _.isNil(x))
            .sort()
            .value();
    }, [mediaList, selectedTypeList, allMediaTypes]);

    useEffect(() => {
        if (onSearchChanged) {
            onSearchChanged(selectedTypeList);
        }
    }, [selectedTypeList, onSearchChanged]);

    const handleOnSelect = (
        item: WorkOrderPhotoTypeAPI | WorkOrderVideoTypeAPI
    ) => {
        setSelectedTypeList(prev => [...(prev ?? []), item]);
    };

    return (
        <TypeAheadSearchBar
            options={searchOptions}
            onChange={handleOnSelect}
            disabled={disabled}
            noOptionsText='No results found'
        />
    );
};

export default DocumentRequirementsPhotoSearch;
